import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Card, Input, Button, Modal } from '../../Components/common/UI';
import { toast } from 'react-toastify';
import logo from "./../../Assets/logo.png";
import { authService } from '../../services/authService';
import { useGoogleLogin } from '@react-oauth/google';
import isoCertificate from "./../../Assets/1683623128424.png";

// import previewImage from "./../../Assets/preview.png";
import previewImage1 from "./../../Assets/preview1.svg";
import unnamedGif from "./../../Assets/unnamed.gif";

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotData, setForgotData] = useState({ username: '', newPassword: '', otp: '' });
  const [forgotStep, setForgotStep] = useState('request'); // 'request' | 'verify'
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  const from = '/dashboard';

  const adminContacts = [
    { name: "Arup Dutta", phone: "9007127283" },
    { name: "Pradip Nag", phone: "9831090288" },
    { name: "Rajib Roy", phone: "9830700826" }
  ];

  const listVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData);
      if (result.success) {
        if (result.user && result.user.isforgotpassword === 1) {
          toast.info('Password reset required. You will be redirected to reset your password.');
        } else {
          toast.success('Login successful!');
        }
        navigate(from, { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedback.trim()) return;

    setFeedbackLoading(true);
    try {
      await authService.submitFeedback(feedback);
      toast.success('Feedback submitted successfully!');
      setFeedback('');
      setShowFeedbackModal(false);
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!forgotData.username.trim() || !forgotData.newPassword.trim()) {
      toast.error('Please enter both username and new password');
      return;
    }
    setForgotLoading(true);
    setForgotMessage('');
    try {
      const res = await authService.sendForgotPasswordOTP(forgotData.username);
      setForgotMessage(res.data.message);
      setForgotStep('verify');
      toast.success('OTP sent to your registered email!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!forgotData.otp.trim()) return;
    setForgotLoading(true);
    try {
      await authService.resetPasswordWithOTP({
        username: forgotData.username,
        otp: forgotData.otp,
        newPassword: forgotData.newPassword
      });
      toast.success('Password reset successfully! You can now login.');
      setShowForgotModal(false);
      setForgotData({ username: '', newPassword: '', otp: '' });
      setForgotStep('request');
      setForgotMessage('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setForgotLoading(false);
    }
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setForgotData({ username: '', newPassword: '', otp: '' });
    setForgotStep('request');
    setForgotMessage('');
  };

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSuccess = async (tokenResponse) => {
    setGoogleLoading(true);
    try {
      // Exchange access_token for id_token via Google's tokeninfo endpoint
      const infoRes = await fetch(
        `https://www.googleapis.com/oauth2/v3/userinfo`,
        { headers: { Authorization: `Bearer ${tokenResponse.access_token}` } }
      );
      const profile = await infoRes.json();

      if (!profile.email || !profile.email.endsWith('@qjconsulting.in')) {
        toast.error('Only qj consulting accounts are allowed');
        setGoogleLoading(false);
        return;
      }

      // Send the access token to backend for server-side verification
      const result = await googleLogin(tokenResponse.access_token);
      if (result.success) {
        toast.success('Google login successful!');
        navigate(from, { replace: true });
      } else {
        toast.error(result.error);
      }
    } catch {
      toast.error('Google login failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const signInWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => toast.error('Google sign-in was cancelled or failed'),
  });

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <div className="flex-1 flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between w-full max-w-7xl gap-8">

        {/* Left Side: Preview Image (Desktop Only) */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          className="hidden md:block w-4xl"
        >
          <span className="max-w-5xl text-md text-orange-600 italic mb-2 block text-center">
            " Rebooting Enterprise Intelligence "
          </span>
          <span className="max-w-5xl text-sm text-gray-500 italic mb-2 block text-center">
            <b>QJ Consulting</b> is a product-led solutions provider that empowers enterprises to transform their business applications. We deliver 90% of the quality of global leaders at a fraction of the cost, with faster implementation, pragmatic design, and tools built for emerging market realities.
          </span>
          <img
            src={previewImage1}
            alt="System Preview"
            className="w-full h-auto"
          />
          <div className="text-center">
            <p className="text-md text-black">
              Tell us about your thoughts anonymously{" "}
              <button
                onClick={() => setShowFeedbackModal(true)}
                className="font-bold text-blue-700 hover:text-blue-500 cursor-pointer"
              >
                here
              </button>
            </p>
          </div>
        </motion.div>

        <div className="ml-0 md:ml-10 max-w-md w-full space-y-8 md:w-1/2 px-4 md:px-0">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center">
              {/* Image */}
              <img
                src={logo}
                alt="QJ Logo"
                className="mx-auto w-auto h-20 mb-4"
              />

              {/* <h1 className="text-3xl font-bold text-blue-600 mb-2">QJ HRMS</h1> */}
              {/* <h2 className="text-2xl font-bold text-gray-900">Sign in to your account</h2> */}
              <p className="mt-2 text-md font-bold text-blue-600">
                Office, Employee & Work Management System
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  label="Username"
                  name="username"
                  type="text"
                  required
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Enter your username"
                />

                <div className="relative">
                  <Input
                    label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-2/3 transform -translate-y-1/3 flex items-center justify-center w-6 h-6 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-opacity-50 rounded transition-colors duration-200"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <span className="px-1 text-green-600 text-sm"># First Time Password: <b className="text-blue-500">welcome</b></span>

                <Button
                  type="submit"
                  className="w-full"
                  loading={loading}
                  disabled={loading}
                >
                  Sign in
                </Button>
              </form>

              {/* Google Sign-In */}
              <div className="mt-4">
                <div className="relative mb-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-400">or</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => signInWithGoogle()}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-medium text-sm hover:border-gray-400 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {googleLoading ? (
                    <svg className="w-5 h-5 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Sign in with Google
                </button>
                <p className="mt-2 text-center text-xs text-red-400">
                  Only <span className="font-medium text-blue-500">qj-consulting</span> accounts are allowed
                </p>
              </div>

              {/* <div className="mt-6">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-green-600">
                      Don't have an account?{' '}
                      <button onClick={() => setShowModal(true)} className="font-medium text-blue-600 hover:text-blue-500 cursor-pointer">
                        Contact Administrator
                      </button>
                    </span>
                  </div>
                </div>
              </div> */}

              <div className="mt-6 text-center">
                <p className="text-sm text-black">
                  <button
                    onClick={() => setShowForgotModal(true)}
                    className="text-blue-600 cursor-pointer"
                  >
                    forgot password?
                  </button>
                </p>
              </div>
            </Card>
          </motion.div>

        </div>
      </div>
      </div>
      {/* Full-width Footer */}
      <footer className="w-full border-t border-gray-200 bg-white flex flex-col sm:flex-row items-center justify-between">
        <div className="flex-1 max-w-7xl mx-auto px-6 py-2 flex flex-col col-11 sm:flex-row items-center justify-between gap-3">
          {/* ISO Certificate */}
          <div className="flex items-center gap-3">
            <img
              src={isoCertificate}
              alt="ISO 27001 Certified"
              className="h-12 w-12 object-contain drop-shadow-md"
            />
            <div className="text-left">
              <p className="text-xs font-semibold text-gray-700 leading-tight">ISO/IEC 27001 Certified</p>
              <p className="text-xs text-gray-500">Information Security Management</p>
            </div>
          </div>

          {/* Copyright */}
          <p className="text-xs text-orange-600 text-center">
            © 2025 – {new Date().getFullYear()} QJ HRMS | QJ Consulting. All rights reserved.
          </p>

          {/* Links */}
          <div className="flex items-center gap-4">
            <a
              href="https://www.linkedin.com/company/qjconsulting"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#0a66c2] transition-colors"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              LinkedIn
            </a>
            <span className="text-gray-300">|</span>
            <a
              href="https://www.qjconsulting.in"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              www.qjconsulting.in
            </a>
          </div>
        </div>
        <div className="flex-shrink-0 px-6 py-2 flex items-center justify-center">
          <img
            src={unnamedGif}
            alt="QJ HRMS"
            className="h-10 object-contain"
          />
        </div>
      </footer>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <div className="p-4">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-medium text-blue-600">Contact Administrator</h3>
            <button
              onClick={() => setShowModal(false)}
              className="ml-auto flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100"
            >
              <span className="sr-only">Close</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <motion.ul variants={listVariants} initial="hidden" animate="visible" className="space-y-2">
            {adminContacts.map((contact, index) => (
              <motion.li key={index} variants={itemVariants} className="flex justify-between">
                <span className="font-medium">{contact.name}</span>
                <span>{contact.phone}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </Modal>

      <Modal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} maxWidth="max-w-3xl">
        <div className="p-2">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-medium text-blue-600">Tell About Your Thoughts Anonymously to <span className="text-orange-600">QJ Consulting</span></h3>
            <button
              onClick={() => setShowFeedbackModal(false)}
              className="ml-auto flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100"
            >
              <span className="sr-only">Close</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="bg-yellow-50 text-yellow-800 p-3 rounded text-md mb-4">
            Your thoughts are completely anonymous. No personal data (user ID, IP, etc.) will be stored with this submission. - <b>We value your honest feedback to help us improve!</b>
          </div>
          <form onSubmit={handleFeedbackSubmit}>
            <textarea
              className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 min-h-[200px]"
              placeholder="Convey your thoughts here..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              required
            ></textarea>
            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="secondary" onClick={() => setShowFeedbackModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={feedbackLoading} disabled={!feedback.trim() || feedbackLoading}>
                Submit
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      <Modal isOpen={showForgotModal} onClose={closeForgotModal} maxWidth="max-w-xl">
        <div className="p-2">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-medium text-blue-600">Forgot Password</h3>
            <button
              onClick={closeForgotModal}
              className="ml-auto flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full hover:bg-gray-100"
            >
              <span className="sr-only">Close</span>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {forgotMessage && (
            <div className="bg-green-50 text-green-700 p-3 rounded text-sm mb-4">
              {forgotMessage}
            </div>
          )}

          <form onSubmit={forgotStep === 'request' ? handleSendOTP : handleResetPassword} className="space-y-4">
            <Input
              label="Username"
              type="text"
              required
              value={forgotData.username}
              onChange={(e) => setForgotData({ ...forgotData, username: e.target.value })}
              placeholder="Enter your username"
              disabled={forgotStep === 'verify'}
            />

            <div className="relative">
              <Input
                label="New Password"
                type={showNewPassword ? "text" : "password"}
                required
                value={forgotData.newPassword}
                onChange={(e) => setForgotData({ ...forgotData, newPassword: e.target.value })}
                placeholder="Enter new password"
                disabled={forgotStep === 'verify'}
              />
              <button
                type="button"
                className="absolute right-3 top-2/3 transform -translate-y-1/3 flex items-center justify-center w-6 h-6 text-gray-500 hover:text-gray-700 focus:outline-none rounded transition-colors duration-200"
                onClick={() => setShowNewPassword(!showNewPassword)}
                tabIndex={-1}
              >
                {showNewPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            {forgotStep === 'verify' && (
              <Input
                label="Enter OTP"
                type="text"
                required
                maxLength={6}
                value={forgotData.otp}
                onChange={(e) => setForgotData({ ...forgotData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                placeholder="Enter 6-digit OTP"
              />
            )}

            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="secondary" onClick={closeForgotModal}>
                Cancel
              </Button>
              {forgotStep === 'request' ? (
                <Button
                  type="submit"
                  variant="primary"
                  loading={forgotLoading}
                  disabled={!forgotData.username.trim() || !forgotData.newPassword.trim() || forgotLoading}
                >
                  Get OTP
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="primary"
                  loading={forgotLoading}
                  disabled={forgotData.otp.length !== 6 || forgotLoading}
                >
                  Reset Password
                </Button>
              )}
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

export default Login;
